import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQuery } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { Modal, Input, InputNumber, Button, message, Spin, Card, Row, Col, Space, Divider } from 'antd'
import { PlusOutlined, LeftOutlined, RightOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { ADD_ORDER_SERVICE_LINE_ITEM } from '../../graphql/addOrderServiceLineItem'
import { GET_SERVICES } from '../../graphql/services'

const { Search } = Input

const AddServiceLineItemForm = ({ visible, onCancel, onSuccess, orderNumber, i18n }) => {
  const [submitting, setSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedService, setSelectedService] = useState(null)
  const [quantity, setQuantity] = useState(1)

  const { data: servicesData, loading: servicesLoading, refetch } = useQuery(GET_SERVICES, {
    variables: { first: 9, after: null, last: null, before: null, excludeIds: [], query: searchQuery },
    skip: !visible,
    fetchPolicy: 'network-only'
  })

  const [addServiceLineItem] = useMutation(ADD_ORDER_SERVICE_LINE_ITEM, {
    onCompleted: (data) => {
      setSubmitting(false)
      if (data.addOrderServiceLineItem.errors) {
        message.error(`${i18n.t('inquiries:addServiceLineItemFailed')}: ${data.addOrderServiceLineItem.errors}`)
      } else {
        message.success(i18n.t('inquiries:addServiceLineItemSuccess'))
        onSuccess()
      }
    },
    onError: () => {
      setSubmitting(false)
      message.error(i18n.t('inquiries:addServiceLineItemFailed'))
    }
  })

  const handleSelectService = (service) => {
    setSelectedService(service)
    setQuantity(1)
  }

  const handleConfirmAdd = () => {
    if (!selectedService) return
    
    setSubmitting(true)
    addServiceLineItem({
      variables: {
        orderNumber: orderNumber,
        serviceExternalId: selectedService.id,
        quantity: quantity
      }
    })
  }

  const handleCancelSelection = () => {
    setSelectedService(null)
    setQuantity(1)
  }

  const handleCancel = () => {
    setSearchQuery('')
    setSelectedService(null)
    setQuantity(1)
    onCancel()
  }

  const handleSearch = (value) => {
    setSearchQuery(value)
    refetch({
      first: 9,
      after: null,
      last: null,
      before: null,
      excludeIds: [],
      query: value
    })
  }

  const handleNextPage = () => {
    const pageInfo = servicesData && servicesData.services && servicesData.services.pageInfo
    if (pageInfo && pageInfo.hasNextPage) {
      refetch({
        first: 9,
        after: pageInfo.endCursor,
        last: null,
        before: null,
        excludeIds: [],
        query: searchQuery
      })
    }
  }

  const handlePreviousPage = () => {
    const pageInfo = servicesData && servicesData.services && servicesData.services.pageInfo
    if (pageInfo && pageInfo.hasPreviousPage) {
      refetch({
        first: null,
        after: null,
        last: 9,
        before: pageInfo.startCursor,
        excludeIds: [],
        query: searchQuery
      })
    }
  }

  const services = (servicesData && servicesData.services && servicesData.services.edges) || []
  const pageInfo = (servicesData && servicesData.services && servicesData.services.pageInfo) || {}

  return (
    (<Modal
      title={i18n.t('inquiries:addServiceLineItem')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={900}
      bodyStyle={{ padding: '20px' }}
    >
      {selectedService ? (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: 4 }}>
                ID: {selectedService.id}
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: 8 }}>
                {selectedService.title}
              </div>
              <div style={{ fontSize: '18px', color: '#52c41a', fontWeight: 'bold' }}>
                ฿{selectedService.price}
                {selectedService.unit && (
                  <span style={{ fontSize: '14px', color: '#999', marginLeft: 8 }}>
                    / {selectedService.unit}
                  </span>
                )}
              </div>
            </div>
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                {i18n.t('inquiries:quantity')}
              </label>
              <InputNumber
                min={0.01}
                step={1}
                value={quantity}
                onChange={(value) => setQuantity(value || 1)}
                style={{ width: '100%' }}
                size="large"
              />
            </div>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancelSelection} icon={<CloseOutlined />}>
                {i18n.t('inquiries:cancel')}
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmAdd}
                loading={submitting}
                icon={<PlusOutlined />}
              >
                {i18n.t('inquiries:addServiceLineItem')}
              </Button>
            </Space>
          </Card>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Search
              placeholder={i18n.t('inquiries:searchService')}
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              loading={servicesLoading}
            />
          </div>

          <Divider style={{ margin: '16px 0' }} />

      <div style={{ marginBottom: 16 }}>
        <strong>{i18n.t('inquiries:availableServices')}</strong>
        <Space style={{ float: 'right' }}>
          <Button
            icon={<LeftOutlined />}
            onClick={handlePreviousPage}
            disabled={!pageInfo.hasPreviousPage || servicesLoading}
            size="small"
          />
          <Button
            icon={<RightOutlined />}
            onClick={handleNextPage}
            disabled={!pageInfo.hasNextPage || servicesLoading}
            size="small"
          />
        </Space>
      </div>

      {servicesLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : services.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          {i18n.t('inquiries:noServicesFound')}
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {services.map((edge) => {
            const service = edge.node
            return (
              <Col xs={24} sm={12} md={8} key={service.id}>
                <Card
                  size="small"
                  style={{ height: '100%' }}
                  bodyStyle={{ padding: '12px' }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      ID: {service.id}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: 4 }}>
                      {service.title}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: '16px', color: '#52c41a', fontWeight: 'bold' }}>
                      ฿{service.price}
                    </div>
                    {service.unit && (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {service.unit}
                      </div>
                    )}
                  </div>
                  <Button
                    type="primary"
                    block
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleSelectService(service)}
                    loading={submitting}
                    danger
                  >
                    {i18n.t('inquiries:select')}
                  </Button>
                </Card>
              </Col>
            )
          })}
        </Row>
          )}
        </>
      )}
    </Modal>)
  );
}

AddServiceLineItemForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  orderNumber: PropTypes.string.isRequired,
  i18n: PropTypes.object.isRequired
}

export default withTranslation('inquiries')(AddServiceLineItemForm)
